import apiClient from '../apiClient'
import paymentConfig from '@/config/payment.config'

// get the transaction by id
const getTransactionById = async (transactionId) => {
    try {
        const response = await apiClient.get(`/transactions/${transactionId}`)
        return response.data
    } catch (error) {
        console.error('Failed to retrieve transaction by id: ' + error.message)
        return null
    }
}

const getUserTransactions = async (limit = 20, skip = 0, type = null) => {
    try {
        const params = {
            limit,
            skip,
        }
        if (type) {
            params.type = type
        }
        const response = await apiClient.get(`/transactions/myTransactions`, {
            params,
        })
        return response.data || []
    } catch (error) {
        console.error('Failed to retrieve user transactions: ' + error.message)
        return []
    }
}

// Utility function to dynamically load Stripe.js
let stripeScriptLoaded = false
const loadStripeScript = () => {
    return new Promise((resolve, reject) => {
        if (stripeScriptLoaded) {
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = 'https://js.stripe.com/basil/stripe.js'
        script.onload = () => {
            stripeScriptLoaded = true
            resolve()
        }
        script.onerror = (error) => {
            reject(new Error('Failed to load Stripe script: ' + error))
        }
        document.head.appendChild(script)
    })
}

const createPayin = async (
    packId,
    tracker,
    paymentProcessor = paymentConfig.paymentProcessor,
    paymentMethod = paymentConfig.paymentMethod,
    amount = 0,
    adcashClickId = null
) => {
    try {
        const response = await apiClient.post('/transactions/payin', {
            packId,
            tracker,
            paymentProcessor,
            paymentMethod,
            idempotencyKey: crypto.randomUUID(),
            successUrl: window.location.href,
            returnUrl: window.location.href,
            cancelUrl: window.location.href,
            adcashClickId,
        })

        console.log('=== response createPayin ===', response.data)

        if (paymentProcessor === 'cardbilling') {
            // For CardBilling, simply redirect to the provided checkout URL
            window.location.href = response.data.data.checkoutUrl
        } else if (paymentProcessor === 'stripe') {
            // For Stripe, use the existing flow
            const sessionId = response.data.data.externalTransactionId
            console.log('=== sessionId ===', sessionId)
            await loadStripeScript()
            const stripe = window.Stripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)
            await stripe.redirectToCheckout({ sessionId: sessionId })
        }

        // Example response data:
        // {
        //     "success": true,
        //     "message": "Payin transaction created and pending payment confirmation",
        //     "data": {
        //         "transaction": {
        //             "type": "payin",
        //             "status": "pending",
        //             "clientId": "685142699466ebc049df8126",
        //             "version": 0,
        //             "totalAmountUSD": 90,
        //             "entryIds": [],
        //             "reference": {
        //                 "kind": "Stars",
        //                 "item": "684336081e49b2294c0f1ac0",
        //                 "_id": "68557a0a66fe4767d17197b1"
        //             },
        //             "idempotencyKey": "1ba9bbee-a468-4122-ba69-bdce29c6e693",
        //             "metadata": {
        //                 "starsPurchased": 500,
        //                 "packagePrice": 90,
        //                 "starValue": 0.1,
        //                 "packId": "684336081e49b2294c0f1ac0",
        //                 "packDetails": {
        //                     "name": "500 Stars Pack",
        //                     "description": null,
        //                     "pricePerStar": 0.18
        //                 },
        //                 "checkoutUrl": "https://checkout.stripe.com/c/pay/cs_test_a1160KWNSJdHawP0V28sjYtZizNNlvLf7CrKG6MGmvtNHYFY4ZIKfdEhI8#fidkdWxOYHwnPyd1blpxYHZxWjA0V0I3XVNXdUhqR0tXMF1mYDRuTzVrNTFyZ1FcbF1RM0J1TEdrc0ZDYDE9ZD03SXZHTEs1SUBtdWhfbmpCMmtWfTAwM2BxbGw1YGFoYzVhc09GPFFxPEJkNTVnNEthczRkfycpJ2N3amhWYHdzYHcnP3F3cGApJ2lkfGpwcVF8dWAnPyd2bGtiaWBabHFgaCcpJ2BrZGdpYFVpZGZgbWppYWB3dic%2FcXdwYHgl"
        //             },
        //             "audit": {
        //                 "createdBy": "685142699466ebc049df8126",
        //                 "statusChanges": []
        //             },
        //             "_id": "68557a0a66fe4767d17197b0",
        //             "createdAt": "2025-06-20T15:11:06.703Z",
        //             "updatedAt": "2025-06-20T15:11:06.703Z",
        //             "__v": 0
        //         },
        //         "pack": {
        //             "_id": "684336081e49b2294c0f1ac0",
        //             "stars": 500,
        //             "price": 90,
        //             "currency": "usd",
        //             "active": true,
        //             "description": null,
        //             "featured": false,
        //             "displayOrder": 999,
        //             "createdAt": "2025-06-06T18:40:08.906Z",
        //             "updatedAt": "2025-06-06T18:40:08.906Z",
        //             "__v": 0,
        //             "pricePerStar": 0.18,
        //             "id": "684336081e49b2294c0f1ac0"
        //         },
        //         "checkoutUrl": "https://checkout.stripe.com/c/pay/cs_test_a1160KWNSJdHawP0V28sjYtZizNNlvLf7CrKG6MGmvtNHYFY4ZIKfdEhI8#fidkdWxOYHwnPyd1blpxYHZxWjA0V0I3XVNXdUhqR0tXMF1mYDRuTzVrNTFyZ1FcbF1RM0J1TEdrc0ZDYDE9ZD03SXZHTEs1SUBtdWhfbmpCMmtWfTAwM2BxbGw1YGFoYzVhc09GPFFxPEJkNTVnNEthczRkfycpJ2N3amhWYHdzYHcnP3F3cGApJ2lkfGpwcVF8dWAnPyd2bGtiaWBabHFgaCcpJ2BrZGdpYFVpZGZgbWppYWB3dic%2FcXdwYHgl",
        //         "externalTransactionId": "cs_test_a1160KWNSJdHawP0V28sjYtZizNNlvLf7CrKG6MGmvtNHYFY4ZIKfdEhI8",
        //         "paymentSession": {
        //             "sessionId": "cs_test_a1160KWNSJdHawP0V28sjYtZizNNlvLf7CrKG6MGmvtNHYFY4ZIKfdEhI8",
        //             "url": "https://checkout.stripe.com/c/pay/cs_test_a1160KWNSJdHawP0V28sjYtZizNNlvLf7CrKG6MGmvtNHYFY4ZIKfdEhI8#fidkdWxOYHwnPyd1blpxYHZxWjA0V0I3XVNXdUhqR0tXMF1mYDRuTzVrNTFyZ1FcbF1RM0J1TEdrc0ZDYDE9ZD03SXZHTEs1SUBtdWhfbmpCMmtWfTAwM2BxbGw1YGFoYzVhc09GPFFxPEJkNTVnNEthczRkfycpJ2N3amhWYHdzYHcnP3F3cGApJ2lkfGpwcVF8dWAnPyd2bGtiaWBabHFgaCcpJ2BrZGdpYFVpZGZgbWppYWB3dic%2FcXdwYHgl",
        //             "amount": 90,
        //             "currency": "usd",
        //             "status": "pending"
        //         }
        //     }
        // }

        return response.data
    } catch (error) {
        console.error('Failed to create payin: ' + error.message)
    }
}

// Verify transaction with payment processor
const verifyTransaction = async (transactionId) => {
    try {
        const response = await apiClient.get(
            `/transactions/${transactionId}/verify`
        )
        return response.data
    } catch (error) {
        console.error('Failed to verify transaction: ' + error.message)
        return null
    }
}

// Cancel transaction
const cancelTransaction = async (transactionId) => {
    try {
        const response = await apiClient.post(
            `/transactions/${transactionId}/cancel`
        )
        return response.data
    } catch (error) {
        console.error('Failed to cancel transaction: ' + error.message)
        return null
    }
}

// NOTE: createPayout has been removed as manual withdrawals are no longer available for creators.
// Creators now receive automatic weekly payouts every Tuesday.

export default {
    getTransactionById,
    getUserTransactions,
    createPayin,
    verifyTransaction,
    cancelTransaction,
}
